import React, {useEffect, useMemo} from "react";
import {useExpanded, useFilters, useGlobalFilter, usePagination, useRowSelect, useSortBy, useTable} from "react-table";
// A great library for fuzzy filtering/sorting items
import {Pagination, Tooltip} from "@mui/material";
import {ArrowDropDown, ArrowDropUp, InfoSharp} from "@mui/icons-material";
import Checkbox from "@mui/material/Checkbox";
import {debounce} from "lodash";
import GridColumnsControl from "./GridControls/GridColumnsControl";
import CsvDownloadControl from "./GridControls/CsvDownloadControl";
import {formatDate} from "../Utils/dateUtils";
import {DEFAULT_PAGE_SIZE, INVOICE_STATUS_FAILED} from "../Utils/constants";
import {saveLatestFiltersToLocalStorage} from "../Utils/gridUtils";
import {DefaultColumnFilter} from "./GridFilters/DefaultColumnFilter";
import {getFilterTypes} from "../Utils/filterTypes";

const getCellColor = (data, column) => {
    if (column?.includes("invoice_status") && data.invoice_status) {
        if (data.invoice_status === "Pending") {
            var currentDate = new Date();
            var deadline = new Date(data.invoice_deadline_date);

            var yellowDate = new Date(data.invoice_deadline_date);
            yellowDate.setDate(yellowDate.getDate() - 4);

            var orangeDate = new Date(data.invoice_deadline_date);
            orangeDate.setDate(orangeDate.getDate() - 2);

            if (currentDate >= deadline) {
                return "red";
            } else if (currentDate >= orangeDate) {
                return "orange";
            } else if (currentDate >= yellowDate) {
                return "yellow";
            }
        } else {
            return "green";
        }
    }
};

const getCellTextColor = (data, column) => {
    if (column?.includes("invoice_status") && data.invoice_status) {
        if (data.invoice_status === "Pending") {
            var currentDate = new Date();
            var deadline = new Date(data.invoice_deadline_date);

            var yellowDate = new Date(data.invoice_deadline_date);
            yellowDate.setDate(yellowDate.getDate() - 4);

            var orangeDate = new Date(data.invoice_deadline_date);
            orangeDate.setDate(orangeDate.getDate() - 2);

            if (currentDate >= deadline) {
                return "white"; //if background is red
            } else if (currentDate >= orangeDate) {
                return "black";
            } else if (currentDate >= yellowDate) {
                return "black";
            }
        } else {
            return "white"; //if background is green
        }
    }
};

const getCellText = (data, cell, colLinkObj, colJsonObj) => {
    const columnName = cell.column.column;

    if (columnName?.includes("invoice_status") && data.invoice_status === INVOICE_STATUS_FAILED && data?.error_message) {
        return (
            <div>
                {data.invoice_status} &nbsp;
                <Tooltip style={{maxWidth: 600}} title={data?.error_message} arrow placement='top'>
                    <InfoSharp />
                </Tooltip>
            </div>
        );
    }
    
    for (const colLink of colLinkObj || []) {
        if (colLink[columnName]) {
            const { key, link } = colLink[columnName];
            const id = data[key];

            if (id === undefined || id === null) return data[columnName];

            const resolvedLink = link.replace(`:${key}`, id);

            return (
                <a
                    href={resolvedLink.startsWith('http') ? resolvedLink : `https://${resolvedLink}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ textDecoration: 'none', color: '#1976d2' }}
                    >
                    {data[columnName]}
                </a>
            );
        }
    }

    if (colJsonObj !== null && colJsonObj.includes(columnName)) {
        if(data[columnName] !== "" && typeof(data[columnName]) == "string") {
            return formatJson(data[columnName]);
        }
    }

    if (cell.column.hasOwnProperty("formatDate") && cell.column.formatDate) {
        return formatDate(data[columnName]);
    }
    // 3. Default render
    return cell.render("Cell");
};

const formatJson = (jsonString) => {
    const parsedData = JSON.parse(jsonString);
    return Object.entries(parsedData).map(([key, value]) => {
        const titleKey = key
            .toLowerCase()
            .replace(/_/g, ' ')
            .replace(/\b\w/g, char => char.toUpperCase());

        return `${titleKey}: ${value}`;
    }).join(', ');
};

// Table component
const GridElement = ({
                         columns,
                         data,
                         pageCount: controlledPageCount,
                         loading,
                         fetchData,
                         filters,
                         setFilters,
                         controlledPageIndex,
                         setControlledPageIndex,
                         rowsCount,
                         handleRowClick,
                         initialSortBy,
                         sortOrder,
                         hideSelection,
                         refreshGridData,
                         selectableCount,
                         handleRowSelection,
                         validateBeforeSelection,
                         colLinks = null,
                         colJson = null,
                         showColumnsControl = false,
                         showCsvDownloadControl = false,
                         setColumns,
                         table,
                         lastSortBy,
                     }) => {
    const [allRowsSelected, setAllRowsSelected] = React.useState(false);
    const [isRowsSelected, setIsRowsSelected] = React.useState(false);
    const [selectedRows, setSelectedRows] = React.useState({});

    handleRowSelection = handleRowSelection || (() => {
    });

    const toggleSelectAllRows = (e) => {
        const checked = e.target.checked;
        setAllRowsSelected(checked);
        if (checked) {
            setSelectedRows({
                ...selectedRows,
                [controlledPageIndex]: data.map((r) => r[columns[0].accessor]),
            });
        } else if (selectedRows[controlledPageIndex]) {
            setSelectedRows({...selectedRows, [controlledPageIndex]: []});
            setIsRowsSelected(false);
        }
    };

    useEffect(() => {
        const _selectedRows = selectedRows[controlledPageIndex] || [];

        setAllRowsSelected(data.length === _selectedRows.length);
        setIsRowsSelected(_selectedRows.length > 0);
    }, [data]);

    const toggleRowSelect = (id, row) => {
        let _selectedRows = selectedRows[controlledPageIndex]
            ? [...selectedRows[controlledPageIndex]]
            : [];

        let selectedCount = _selectedRows.length;
        let checked = false;

        if (_selectedRows.includes(id)) {
            _selectedRows = _selectedRows.filter((rId) => rId !== id);
            checked = false;
        } else {
            if (selectableCount === 1) {
                //uncheck previous selection and select new row.
                _selectedRows = [id];
            } else {
                if (selectedCount < selectableCount) {
                    _selectedRows.push(id);
                }
            }

            checked = true;
        }

        if (validateBeforeSelection) {
            if (validateBeforeSelection(row.original, checked)) return
        }

        setAllRowsSelected(_selectedRows.length === data.length);
        setSelectedRows({
            ...selectedRows,
            [controlledPageIndex]: _selectedRows,
        });
        setIsRowsSelected(_selectedRows.length > 0);
        handleRowSelection(checked, id);
    };

    let filterTypes = React.useMemo(() => getFilterTypes(), []);
    const defaultColumn = React.useMemo(
        () => ({
            Filter: DefaultColumnFilter,  // Let's set up our default Filter UI
        }),
        []
    );

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        state,
        page,
        pageCount,
        gotoPage,
        setPageSize,
        // Get the state from the instance
        state: {pageIndex, pageSize, sortBy},
    } = useTable(
        {
            columns,
            data,
            defaultColumn, // Be sure to pass the defaultColumn option
            filterTypes,
            initialState: {
                pageIndex: controlledPageIndex,
                pageSize: DEFAULT_PAGE_SIZE,
                filters,
                sortBy: lastSortBy ? lastSortBy : [
                    {
                        id: initialSortBy,
                        desc: sortOrder === "desc",
                    },
                ],
            }, // if you don't set that pageIndex a dynamic value, it will re-render to its default value 0.
            manualPagination: true, // Tell the usePagination
            // hook that we'll handle our own data fetching
            // This means we'll also have to provide our own
            // pageCount.
            pageCount: controlledPageCount,
            manualFilters: true,
            manualSortBy: true,
        },
        useFilters,
        useGlobalFilter,
        useSortBy,
        useExpanded,
        usePagination,
        useRowSelect,
    );

    // Remove the checkbox column from rendering
    const hiddenColumns = ["_selector"];
    hiddenColumns.forEach((columnId) => {
        const index = columns.findIndex((column) => column.id === columnId);
        if (index !== -1) {
            columns.splice(index, 1);
        }
    });

    const debounceFetchData = useMemo(() => debounce(fetchData, 1100), []);

    // set the filters to reload the grid whenever search is performed on any of the column
    React.useEffect(() => {
        const timer = setTimeout(() => {
            setFilters(state.filters);
            setControlledPageIndex(pageIndex);
        }, 300); // 300ms debounce delay

        // Cleanup the timeout on value change or component unmount
        return () => clearTimeout(timer);
    }, [state.filters])

    // refresh the grid whenever grid is set to refresh manually i.e. on delete/update
    React.useEffect(() => {
        refreshGridData && fetchData &&
        debounceFetchData({
            pageIndex,
            pageSize,
            sortBy,
            filters,
            columns,
        });
    }, [refreshGridData])

    // Listen for changes in pagination and use the state to fetch our new data
    React.useEffect(() => {
        fetchData &&
        debounceFetchData({
            pageIndex,
            pageSize,
            sortBy,
            filters,
            columns,
        });

        if (filters?.length && table) { //this change will only work for modules with new filtering logic. (&& table) should be removed when implemented for all modules
            saveLatestFiltersToLocalStorage(table, filters, sortBy);
        }

        if (sortBy?.length && table) { //this change will only work for modules with new filtering logic. (&& table) should be removed when implemented for all modules
            saveLatestFiltersToLocalStorage(table, filters, sortBy);
        }
    }, [
        fetchData,
        pageIndex,
        pageSize,
        sortBy,
        setFilters,
        columns,
        filters
    ]);

    const handleColumnToggle = (columnKey) => {
        setColumns(prevColumns =>
            prevColumns.map(column =>
                column.column === columnKey
                    ? { ...column, visible: !column.visible }
                    : column
            )
        );
    };

    const handleColumnReorder = (reorderedColumns) => {
        setColumns(reorderedColumns);
    };

    return (
        <div className="react-table-container">
            <div
                style={{
                    display: "flex",
                    justifyContent: "right",
                    marginBottom: "10px",
                }}
            >
                { showCsvDownloadControl &&
                    <CsvDownloadControl page={page} headerGroups={headerGroups}/>
                }

                {
                    showColumnsControl &&
                    <GridColumnsControl
                        columns={columns}
                        onColumnToggle={handleColumnToggle}
                        onColumnReorder={handleColumnReorder}
                        table={table}/>
                }
            </div>
            <div className="react-table-wrapper">
                <table {...getTableProps()}>
                    <thead>
                    {headerGroups.map((headerGroup) => (
                        <tr
                            style={{background: "#f3f3f3"}}
                            {...headerGroup.getHeaderGroupProps()}
                        >
                            {!hideSelection && (
                                <th
                                    style={{
                                        minWidth: "0",
                                        paddingLeft: "1rem",
                                        paddingRight: "1rem",
                                    }}
                                >
                                    {selectableCount > 1 && <Checkbox
                                        indeterminate={
                                            !allRowsSelected &&
                                            isRowsSelected
                                        }
                                        checked={allRowsSelected}
                                        onClick={toggleSelectAllRows}
                                    />}
                                </th>
                            )}
                            {headerGroup.headers.map((column) => {
                                if (column.hasOwnProperty("visible")) {
                                    return (
                                        column.visible && <th
                                            {...column.getHeaderProps(
                                                column.getSortByToggleProps()
                                            )}
                                        >
                                            <div>
                                                {column.render("Header")}
                                                {/* Add a sort direction indicator */}
                                                <span>
                                                {column.isSorted ? (
                                                    column.isSortedDesc ? (
                                                        <ArrowDropDown
                                                            sx={{
                                                                marginLeft:
                                                                    "5px",
                                                            }}
                                                        />
                                                    ) : (
                                                        <ArrowDropUp
                                                            sx={{
                                                                marginLeft:
                                                                    "5px",
                                                            }}
                                                        />
                                                    )
                                                ) : (
                                                    ""
                                                )}
                                            </span>
                                            </div>
                                        </th>
                                    );
                                } else {
                                    return (
                                        <th
                                            {...column.getHeaderProps(
                                                column.getSortByToggleProps()
                                            )}
                                        >
                                            <div>
                                                {column.render("Header")}
                                                {/* Add a sort direction indicator */}
                                                <span>
                                                {column.isSorted ? (
                                                    column.isSortedDesc ? (
                                                        <ArrowDropDown
                                                            sx={{
                                                                marginLeft:
                                                                    "5px",
                                                            }}
                                                        />
                                                    ) : (
                                                        <ArrowDropUp
                                                            sx={{
                                                                marginLeft:
                                                                    "5px",
                                                            }}
                                                        />
                                                    )
                                                ) : (
                                                    ""
                                                )}
                                            </span>
                                            </div>
                                        </th>
                                    )
                                }
                            })}
                        </tr>
                    ))}
                    {headerGroups.map((headerGroup) => (
                        <tr {...headerGroup.getHeaderGroupProps()}>
                            {!hideSelection && (
                                <th style={{minWidth: "0"}}></th>
                            )}
                            {headerGroup.headers.map((column) =>
                                {
                                    if (column.hasOwnProperty("visible")) {
                                        return (
                                            column.visible && <th {...column.getHeaderProps()}>
                                            {/* Render the columns filter UI */}
                                            <div>
                                                {
                                                    column.canFilter
                                                    ? column.render("Filter")
                                                    : null}
                                            </div>
                                        </th>
                                        )
                                    } else {
                                        return (
                                            <th {...column.getHeaderProps()}>
                                                {/* Render the columns filter UI */}
                                                <div>
                                                    {column.canFilter
                                                        ? column.render("Filter")
                                                        : null}
                                                </div>
                                            </th>
                                        )
                                    }
                                }
                            )}
                        </tr>
                    ))}
                    </thead>
                    {loading ? (
                        <tbody {...getTableBodyProps()}>
                        <tr>
                            <td colSpan="10000">Loading...</td>
                        </tr>
                        </tbody>
                    ) : (
                        <tbody {...getTableBodyProps()}>
                        {!page.length ? (
                            <tr>
                                <td
                                    colSpan={100}
                                    style={{textAlign: "center"}}
                                >
                                    No record found{" "}
                                </td>
                            </tr>
                        ) : (
                            page.map((row) => {
                                prepareRow(row);
                                return (
                                    <React.Fragment key={row.id}>
                                        <tr {...row.getRowProps()}>
                                            {!hideSelection && (
                                                <td style={{minWidth: "0"}}>
                                                    {!row?.original?.disable_row_selection && (
                                                        <Checkbox
                                                            checked={
                                                                allRowsSelected ||
                                                                !!selectedRows[
                                                                    controlledPageIndex
                                                                    ]?.includes(
                                                                    row.allCells[0]
                                                                        .value
                                                                )
                                                            }
                                                            disabled={selectableCount === 1 && selectedRows[controlledPageIndex]?.length > 0 && !selectedRows[
                                                                controlledPageIndex
                                                                ]?.includes(
                                                                row.allCells[0]
                                                                    .value
                                                            )}
                                                            onClick={() =>
                                                                toggleRowSelect(
                                                                    row.allCells[0]
                                                                        .value, row
                                                                )
                                                            }
                                                        />
                                                    )}
                                                </td>
                                            )}
                                            {row.cells.map((cell) => {
                                                if (cell.column.hasOwnProperty("visible")) {
                                                    return (
                                                        cell.column.visible && <td
                                                            {...cell.getCellProps()}
                                                            onClick={() =>
                                                                handleRowClick(row)
                                                            }
                                                            style={{
                                                                backgroundColor:
                                                                    getCellColor(
                                                                        row.original,
                                                                        cell.column.column
                                                                    ),
                                                                color: getCellTextColor(
                                                                    row.original,
                                                                    cell.column.column
                                                                ),
                                                            }}
                                                        >
                                                            {getCellText(row.original, cell, colLinks, colJson)}
                                                        </td>
                                                    );
                                                } else {
                                                    return (
                                                        <td
                                                            {...cell.getCellProps()}
                                                            onClick={() =>
                                                                handleRowClick(row)
                                                            }
                                                            style={{
                                                                backgroundColor:
                                                                    getCellColor(
                                                                        row.original,
                                                                        cell.column.column
                                                                    ),
                                                                color: getCellTextColor(
                                                                    row.original,
                                                                    cell.column.column
                                                                ),
                                                            }}
                                                        >
                                                            {getCellText(row.original, cell, colLinks, colJson)}
                                                        </td>
                                                    );
                                                }
                                            })}
                                        </tr>
                                        {row.isExpanded ? (
                                            <tr>
                                                {row.cells.map((cell) => {

                                                    let visible = true;

                                                    if (cell.column.hasOwnProperty("visible") && cell.column.visible) {
                                                        visible = cell.column.visible;
                                                    }

                                                    return (
                                                        visible && <td
                                                            {...cell.getCellProps()}
                                                            onClick={() =>
                                                                handleRowClick(row)
                                                            }
                                                            style={{
                                                                backgroundColor:
                                                                    getCellColor(
                                                                        row.original,
                                                                        cell.column.column
                                                                    ),
                                                                color: getCellTextColor(
                                                                    row.original,
                                                                    cell.column.column
                                                                ),
                                                            }}
                                                        >
                                                            {cell.render("Cell")}
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                        ) : null}
                                    </React.Fragment>
                                );
                            })
                        )}
                        </tbody>
                    )}
                </table>
            </div>
            <div
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginBottom: "10px",
                }}
            >
                <div>
                    <span>Total: {rowsCount}</span>
                </div>
                <div className="pagination">
                    <Pagination
                        color="primary"
                        count={pageCount}
                        page={pageIndex + 1}
                        onChange={(_event, value) => {
                            gotoPage(value - 1);
                        }}
                    />
                    <span>
                        | Go to page:{" "}
                        <input
                            type="number"
                            defaultValue={pageIndex + 1}
                            onChange={(e) => {
                                const page = e.target.value
                                    ? Number(e.target.value) - 1
                                    : 0;
                                gotoPage(page);
                            }}
                            style={{width: "100px"}}
                        />
                    </span>{" "}
                    <select
                        style={{minHeight: "25px", marginLeft: "5px"}}
                        value={pageSize}
                        onChange={(e) => {
                            setPageSize(Number(e.target.value));
                        }}
                    >
                        {[100, 250, 500, 1000].map((pageSize) => (
                            <option key={pageSize} value={pageSize}>
                                Show {pageSize}
                            </option>
                        ))}
                    </select>
                </div>
            </div>
        </div>
    );
};

export default GridElement;
